import React, { useState, useEffect } from 'react';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';

const JsonEditorAce = ({  value, onChange }) => {
  
  // Local state to manage the editor content as a string
  const [editorValue, setEditorValue] = useState(() => {
    // Initialize with formatted value only once
    if (!value) return '';
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return value;
      }
    }
    return JSON.stringify(value, null, 2);
  });
  
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // ONLY sync when value changes AND editor is empty or different
  // This prevents overwriting user edits
  useEffect(() => {
    if (!value && !editorValue) return;  // Both empty, no action
    
    // Only update if value actually changed (not just re-render)
    const valueStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (valueStr !== editorValue && !editorValue) {
      // Only update if editor is empty (initial load)
      setEditorValue(valueStr);
    }
  }, [value]);  // Don't include editorValue in deps to avoid loops

  // Handle change event from AceEditor
  const handleEditorChange = (newValue) => {
    // Immediately update local state for responsive typing
    setEditorValue(newValue);
    
    // Clear any existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    // Debounce onChange - only update parent when JSON is valid
    // Increased debounce to 800ms to reduce re-renders during typing
    const timer = setTimeout(() => {
      try {
        // Parse the JSON to ensure it's valid before passing to parent
        const parsedValue = JSON.parse(newValue);
        // Only call onChange if JSON is valid - prevents invalid config from triggering re-renders
        onChange(parsedValue);
      } catch (error) {
        // Don't call onChange for invalid JSON - prevents hooks errors
        // The error will be visible in the editor, but won't break the preview
        console.warn('Invalid JSON in editor (not updating form state):', error.message);
      }
    }, 800); // Increased from 300ms to reduce re-renders
    
    setDebounceTimer(timer);
  };

  // Handle blur event - ensure valid changes are saved when editor loses focus
  const handleBlur = () => {
    // Clear any pending debounce timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
    
    // Try to parse and save current value on blur
    try {
      const parsedValue = JSON.parse(editorValue);
      onChange(parsedValue);
    } catch (error) {
      // If invalid on blur, don't update - user can see the error
      console.warn('Invalid JSON on blur (not updating form state):', error.message);
    }
  };

  // Helper function to suggest fixes for common JSON errors
  const getJSONErrorSuggestion = (errorMessage: string, jsonString: string) => {
    if (errorMessage.includes('Unexpected end of JSON input')) {
      const openBraces = (jsonString.match(/{/g) || []).length;
      const closeBraces = (jsonString.match(/}/g) || []).length;
      const openBrackets = (jsonString.match(/\[/g) || []).length;
      const closeBrackets = (jsonString.match(/]/g) || []).length;
      
      if (openBraces > closeBraces) {
        return `Missing ${openBraces - closeBraces} closing brace(s) }`;
      }
      if (openBrackets > closeBrackets) {
        return `Missing ${openBrackets - closeBrackets} closing bracket(s) ]`;
      }
    }
    
    if (errorMessage.includes('Unexpected token')) {
      return 'Check for extra commas, missing quotes, or unescaped characters';
    }
    
    return 'Check JSON structure for syntax errors';
  };

  return (
    <div>
      <AceEditor
        mode="json"
        theme="monokai"
        value={editorValue} // Use the local state for AceEditor value
        onChange={handleEditorChange}
        onBlur={handleBlur}
        name="json-editor"
        editorProps={{ $blockScrolling: true }}
        setOptions={{ 
          useWorker: false,
          enableBasicAutocompletion: true,
          enableLiveAutocompletion: true,
          enableSnippets: true,
          showLineNumbers: true,
          tabSize: 2,
          useSoftTabs: true,
          wrap: true
        }}
        commands={[
          {
            name: 'save',
            bindKey: { win: 'Ctrl-S', mac: 'Cmd-S' },
            exec: () => { /* Prevent browser save dialog */ }
          }
        ]}
        style={{ width: '100%', height: '400px' }}
        fontSize={14}
      />
    </div>
  );
};

export default JsonEditorAce;
/*import React from 'react';
import { useInput } from 'react-admin';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-monokai';

const JsonEditorAce = ({ source }) => {
  console.log('json editor source', source);
  // Use the `useInput` hook to integrate with react-admin's form state
  const {
    field: { value, onChange },
  } = useInput({ source });

  // Convert value to a formatted JSON string if it's not a string already
  const jsonValue = typeof value === 'string' ? value : JSON.stringify(value, null, 2);

  // Handle change event from AceEditor
  const handleEditorChange = (newValue) => {
    try {
      // Parse the new value to ensure it's valid JSON before updating the form
      const parsedValue = JSON.parse(newValue);
      onChange(parsedValue); // Update the form state
    } catch (error) {
      console.error('Invalid JSON:', error); // Log an error if the JSON is invalid
    }
  };

  return (
    <AceEditor
      mode="json"
      theme="monokai"
      value={jsonValue}
      onChange={handleEditorChange}
      name="json-editor"
      editorProps={{ $blockScrolling: true }}
      setOptions={{ useWorker: false }}
      style={{ width: '100%', height: '400px' }}
    />
  );
};

export default JsonEditorAce;
*/